<template>
  <AppNavbar />
  <div class="container mt-4" id="caja-view">
    <AppHeader title="Sistema de Caja" subtitle="Gestión de Tesorería">
      <template #buttons>
        <template v-if="cajaAbierta">
          <span class="badge bg-dark">Caja Abierta</span>
        </template>
      </template>
    </AppHeader>

    <!-- Acciones rápidas: siempre visibles cuando la caja está abierta -->
  <div v-if="cajaAbierta" class="quick-actions mt-3 mb-4">
      <router-link class="action-tile tile-success" to="/cobro-factura-venta" title="Cobrar pedidos publicados en caja">
        <div class="icon-circle"><i class="bi bi-graph-up"></i></div>
        <div class="tile-text">Cobro de Pedidos Publicados</div>
      </router-link>
      <router-link class="action-tile tile-danger" to="/pago-factura" title="Registrar pago a proveedores">
        <div class="icon-circle"><i class="bi bi-graph-down"></i></div>
        <div class="tile-text">Pago a Proveedores</div>
      </router-link>
      <router-link class="action-tile tile-primary" to="/ingreso-varios" title="Registrar ingreso varios">
        <div class="icon-circle"><i class="bi bi-plus-lg"></i></div>
        <div class="tile-text">Ingreso Varios</div>
      </router-link>
      <router-link class="action-tile tile-warning" to="/egreso-varios" title="Registrar egreso varios">
        <div class="icon-circle"><i class="bi bi-dash-lg"></i></div>
        <div class="tile-text">Egreso Varios</div>
      </router-link>
      <router-link class="action-tile tile-purple" to="/cobro-cliente-credito" title="Cobro a cliente con crédito">
        <div class="icon-circle"><i class="bi bi-credit-card"></i></div>
        <div class="tile-text">Cobro a Cliente Crédito</div>
      </router-link>
    </div>

    <!-- Se ve únicamente si la caja está CERRADA -->
    <div v-if="!cajaAbierta" class="text-center my-5">
      <div class="alert alert-info mx-auto" style="max-width: 500px;">
        <i class="bi bi-info-circle me-2"></i>
        <strong>Caja Cerrada</strong>
        <p class="mb-3 mt-2">{{ cashboxStore.contextMessage || 'No hay una caja abierta para el usuario actual. Debe abrir una caja para comenzar a realizar operaciones de tesorería.' }}</p>
      </div>
      <button class="btn btn-success btn-lg" @click="openModalCaja">
        <i class="bi bi-unlock me-2"></i>Abrir Caja
      </button>
    </div>


    <div v-if="cajaAbierta" class="card p-3 mb-3">
      <h5><i class="bi bi-clock me-2"></i>Sesión Actual</h5>
      <div class="row mt-2">
        <div class="col-md-3"><strong>Usuario</strong><br>{{ cajaData.user }}</div>
        <div class="col-md-3"><strong>Apertura</strong><br>{{ formatoFecha(cajaData.apertura) }}</div>
        <div class="col-md-3"><strong>Monto Inicial</strong><br>{{ formateaNumero(cajaData.montoInicial) }}</div>
        <div class="col-md-3 text-end">
            <router-link
                class="btn btn-outline-secondary"
                :to="{ name: 'CerrarCaja' }"
                >
                Cerrar Caja
            </router-link>
        </div>
      </div>
    </div>

    <div v-if="cajaAbierta" class="row mb-4">
      <div class="col-md-3">
        <div class="card text-center p-3">
          <div class="text-success">Ingresos del Día</div>
          <h5 class="text-success">{{ formateaNumero(cajaData.ingresos_dia) }}</h5>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center p-3">
          <div class="text-danger">Egresos del Día</div>
          <h5 class="text-danger">{{ formateaNumero(cajaData.egresos_dia) }}</h5>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center p-3">
          <div class="text-primary">Saldo en Caja</div>
          <h5 class="text-primary">{{ formateaNumero(cajaData.saldo_en_caja) }}</h5>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center p-3">
          <div class="text-warning">Pendientes de esta Caja</div>
          <h5 class="text-warning">{{ cajaData.facturas_pendientes }}</h5>
        </div>
      </div>
    </div>
    <!-- Ventas a Crédito Pendientes de Cobro -->
    <div v-if="cajaAbierta && creditosPendientes.length" class="mb-4">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h5 class="fw-bold mb-0 text-warning">
          <i class="bi bi-clock-history me-2"></i>Créditos pendientes de cobro
          <span class="badge bg-warning text-dark ms-2">{{ creditosPendientes.length }}</span>
        </h5>
      </div>
      <div v-for="cr in creditosPendientes" :key="cr.id" class="border border-warning rounded p-3 mb-2 d-flex align-items-center justify-content-between">
        <div>
          <div class="fw-semibold">{{ cr.nombre || 'Cliente sin nombre' }}</div>
          <div class="text-muted small">
            {{ cr.comprobante ? `Comprobante: ${cr.comprobante}` : `Mov. #${cr.id}` }}
            · {{ formatFechaHora(cr.fecha) }}
          </div>
        </div>
        <div class="d-flex align-items-center gap-3">
          <div class="fw-bold text-warning">Gs. {{ formateaNumero(cr.monto) }}</div>
          <button
            class="btn btn-outline-danger btn-sm"
            :disabled="anulando === cr.id"
            @click="confirmarAnularCredito(cr)"
          >
            <span v-if="anulando === cr.id" class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
            <i v-else class="bi bi-x-circle me-1"></i>Anular
          </button>
        </div>
      </div>
    </div>

    <AnulacionVentaModal
      v-if="anulacionModalCredito"
      :nombre-cliente="anulacionModalCredito.nombre"
      :monto="anulacionModalCredito.monto"
      :enviando="anulando === anulacionModalCredito.id"
      @cerrado="anulacionModalCredito = null"
      @confirmada="ejecutarAnulacionCredito"
    />

    <!-- Movimientos Recientes -->
    <div v-if="cajaAbierta" class="mb-4">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h5 class="fw-bold mb-0">Movimientos Recientes</h5>
        <router-link class="btn btn-dark" :to="{ name: 'MovimientosCaja' }">Ver Todos</router-link>
      </div>
      <div v-for="mov in movimientosRecientes" :key="mov.id" class="recent-mov-card mb-2 p-3 d-flex align-items-center justify-content-between rounded border">
        <div class="d-flex align-items-center">
            <span v-if="isPositive(mov)" class="me-2 fs-4" :class="getMovementIconClass(mov)"><i class="bi bi-graph-up"></i></span>
            <span v-else-if="isNegative(mov)" class="me-2 fs-4" :class="getMovementIconClass(mov)"><i class="bi bi-graph-down"></i></span>
            <span v-else class="me-2 fs-4" :class="getMovementIconClass(mov)"></span>
          <div>
              <span class="fw-bold" :class="getMovementIconClass(mov)">
                {{ movLabel(mov) }}
                <span v-if="mov.comprobante">#{{ mov.comprobante }}</span>
              </span>
            <span v-if="mov.comprobante" class="badge bg-light text-dark ms-2">Nro mov caja: {{ mov.id }}</span>
            <div class="text-muted small">{{ mov.descripcion }}</div>
          </div>
        </div>
        <div class="text-end">
          <div :class="getMovementIconClass(mov) + ' fw-bold'">
            {{ isPositive(mov) ? '+' : (isNegative(mov) ? '-' : '') }}Gs. {{ formateaNumero(mov.monto) }}
          </div>
          <div class="text-muted small">{{ formatFechaHora(mov.fecha) }}</div>
        </div>
      </div>
    </div>
    <AbrirCajaModal v-model:abierto="modalOpen" @abierta="alAbrirCaja" />

    
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import AppNavbar from '@/components/AppNavbar.vue'
import AppHeader from '@/components/AppHeader.vue'
import AnulacionVentaModal from '@/components/AnulacionVentaModal.vue'
import AbrirCajaModal from '@/components/AbrirCajaModal.vue'
import apiService from '@/services/apiService'
import operationalConfigService from '@/services/operationalConfigService'
import { useCashboxStore } from '@/stores/cashbox'

// Movimientos recientes
const movimientosRecientes = ref([])
// Créditos pendientes de cobro en la sesión actual
const creditosPendientes = ref([])
const anulando = ref(null)
// Venta (cr) que se está anulando vía AnulacionVentaModal — null = modal cerrado.
const anulacionModalCredito = ref(null)

async function fetchMovimientosRecientes() {
  try {
    const res = await apiService.get('/api/cashbox/user-operations?page=1&limit=5')
    if (res.data && res.data.success && Array.isArray(res.data.data)) {
      movimientosRecientes.value = res.data.data.map(mov => ({
        ...mov,
        monto: Number(mov.monto),
        fecha: mov.fecha
      }))
    }
  } catch (err) {
    console.error('Error al obtener movimientos recientes:', err)
  }
}

async function fetchCreditosPendientes() {
  try {
    const res = await apiService.get('/api/cashbox/user-operations?tipo_movimiento=COBRO&estado=PENDIENTE&page=1&limit=50')
    if (res.data && res.data.success && Array.isArray(res.data.data)) {
      creditosPendientes.value = res.data.data
        .filter(m => m.forma === 'CREDITO')
        .map(m => ({ ...m, monto: Number(m.monto) }))
    }
  } catch (err) {
    console.error('Error al obtener créditos pendientes:', err)
  }
}

// Abre AnulacionVentaModal en vez de window.prompt (que no puede pedir dos
// datos: motivo + si la mercadería volvió físicamente). Backend exige
// `devolvio_mercaderia` explícito desde SPEC_DEVOLUCIONES_v0.1.md RF5.
function confirmarAnularCredito(cr) {
  anulacionModalCredito.value = cr
}

async function ejecutarAnulacionCredito({ motivo, devolvio_mercaderia }) {
  const cr = anulacionModalCredito.value
  if (!cr) return

  anulando.value = cr.id
  try {
    await apiService.post('/api/cashbox/cancel-credit-sale', {
      movimiento_id: cr.id,
      motivo,
      devolvio_mercaderia
    })
    creditosPendientes.value = creditosPendientes.value.filter(c => c.id !== cr.id)
    anulacionModalCredito.value = null
    await fetchMovimientosRecientes()
  } catch (err) {
    const msg = err?.response?.data?.message || err?.message || 'Error al anular la venta'
    alert(msg)
    console.error('Error al anular crédito:', err)
  } finally {
    anulando.value = null
  }
}




const cashboxStore = useCashboxStore()
const cajaAbierta = computed(() => cashboxStore.isOpen)
const cajaData    = computed(() => ({
  user        : cashboxStore.nombreUsuario,
  apertura    : cashboxStore.fechaApertura,
  montoInicial: cashboxStore.montoInicial,
  ingresos_dia: cashboxStore.igresos_dia,
  egresos_dia : cashboxStore.egresos_dia,
  saldo_en_caja: cashboxStore.saldo_en_caja,
  facturas_pendientes: cashboxStore.facturas_pendientes
}))
onMounted(() => {
  cashboxStore.fetchCurrentOpen({ sucursal_id: operationalConfigService.getCurrentSucursalId() })
  fetchMovimientosRecientes()
  fetchCreditosPendientes()
  window.addEventListener('app:sucursal-changed', handleGlobalSucursalChange)
})

onBeforeUnmount(() => {
  window.removeEventListener('app:sucursal-changed', handleGlobalSucursalChange)
})

function handleGlobalSucursalChange(event) {
  const sucursalId = Number(event?.detail?.sucursal_id || operationalConfigService.getCurrentSucursalId())
  cashboxStore.resetContext('Contexto de caja limpiado por cambio de establecimiento. Abra una caja para operar caja en este establecimiento.')
  cashboxStore.fetchCurrentOpen({ sucursal_id: sucursalId })
  fetchMovimientosRecientes()
  fetchCreditosPendientes()
  creditosPendientes.value = []
}

// La apertura la resuelve `AbrirCajaModal`: mismo formulario, misma llamada,
// pero disponible tambien desde la pantalla de cobranza (ver el componente).
const modalOpen = ref(false)

function openModalCaja () {
  modalOpen.value = true
}

// Recien abierta la caja, los paneles de sesion salen del store —que el modal
// ya refresco— pero los movimientos y los creditos pendientes son de esta
// pantalla y quedarian vacios hasta recargar.
function alAbrirCaja () {
  fetchMovimientosRecientes()
  fetchCreditosPendientes()
}


// (Se removió el dropdown; acciones ahora están siempre visibles)

/* ---------- helpers de formato ---------- */
function formatoFecha (val) {
  if (!val) return ''
  return new Date(val).toLocaleString('es-PY')   // hora local – formato PY
}
function formateaNumero (n) {
  if (n === null || n === undefined) return ''
  return Number(n).toLocaleString('es-PY', { minimumFractionDigits: 0 })
}

function formatFechaHora(val) {
  if (!val) return ''
  const d = new Date(val)
  return d.toLocaleString('es-PY')
}

// helpers for movement display
function isPositive(mov) {
  // COBRO and INGRESO_VARIO are considered positive (green)
  return mov.tipo_movimiento === 'COBRO' || mov.tipo_movimiento === 'INGRESO_VARIO'
}

function isNegative(mov) {
  // PAGO and EGRESO_VARIO are considered negative (red)
  return mov.tipo_movimiento === 'PAGO' || mov.tipo_movimiento === 'EGRESO_VARIO'
}

/*function isNeutral(mov) {
  // APERTURA is considered neutral
  return mov.tipo_movimiento === 'APERTURA'
}*/

function getMovementIconClass(mov) {
  switch (mov.tipo_movimiento) {
    case 'COBRO':
    case 'INGRESO_VARIO':
      return 'text-success'
    case 'PAGO':
    case 'EGRESO_VARIO':
      return 'text-danger'
    case 'APERTURA':
      return 'text-secondary'
    default:
      return isPositive(mov) ? 'text-success' : 'text-danger'
  }
}

function movLabel(mov) {
  if (mov.tipo_movimiento === 'COBRO') return mov.tipo_operacion === 'VENTA' ? 'Cobro Factura' : 'Cobro'
  if (mov.tipo_movimiento === 'INGRESO_VARIO') return 'Ingreso Varios'
  if (mov.tipo_movimiento === 'PAGO') return 'Pago a Proveedores'
  if (mov.tipo_movimiento === 'EGRESO_VARIO') return 'Egreso Varios'
  if (mov.tipo_movimiento === 'APERTURA') return 'Apertura de Caja'
  return mov.tipo_movimiento
}


</script>

<style scoped>
.recent-mov-card {
  background: #fff;
  border: 1px solid #e5e5e5;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
  transition: box-shadow 0.2s;
}
.recent-mov-card:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}
</style>

<style scoped>
#caja-view .card {
  border-radius: 0.5rem;
}
#caja-view .nav-link.active {
  font-weight: bold;
}

/* Acciones rápidas como “tarjetas/botones” */
.quick-actions {
  display: grid;
  gap: 14px;
  grid-template-columns: 1fr;
}
@media (min-width: 576px) {
  .quick-actions { grid-template-columns: repeat(2, 1fr); }
}
@media (min-width: 768px) {
  .quick-actions { grid-template-columns: repeat(3, 1fr); }
}
@media (min-width: 992px) {
  .quick-actions { grid-template-columns: repeat(4, 1fr); }
}
@media (min-width: 1200px) {
  .quick-actions { grid-template-columns: repeat(5, 1fr); }
}

.action-tile {
  /* variables por defecto */
  --tile-bg: #f8f9fa;
  --tile-border: #e5e5e5;
  --tile-text: #212529;
  --tile-icon-bg: #eef1f4;

  background: var(--tile-bg);
  border: 1px solid var(--tile-border);
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
  padding: 18px 16px;
  height: 96px;
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: var(--tile-text);
  transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease;
}
.action-tile:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  text-decoration: none;
  border-color: rgba(0,0,0,0.08);
}
.action-tile:focus-visible {
  outline: 3px solid rgba(13,110,253,.35);
  outline-offset: 2px;
}

.icon-circle {
  width: 40px;
  height: 40px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--tile-icon-bg);
  font-size: 1.1rem;
}

.tile-text {
  font-weight: 600;
  line-height: 1.2;
}

/* Variantes “soft” */
.tile-success {
  --tile-bg: #f3fcf5;
  --tile-border: #d7f0dc;
  --tile-text: #146c43;
  --tile-icon-bg: #e7f8ea;
}
.tile-danger {
  --tile-bg: #fff4f4;
  --tile-border: #f5d6d6;
  --tile-text: #b02a37;
  --tile-icon-bg: #ffe9e9;
}
.tile-primary {
  --tile-bg: #f2f6ff;
  --tile-border: #d7e3ff;
  --tile-text: #0d6efd;
  --tile-icon-bg: #e7efff;
}
.tile-warning {
  --tile-bg: #fff9ed;
  --tile-border: #ffe4b8;
  --tile-text: #b68200;
  --tile-icon-bg: #fff0cc;
}
.tile-purple {
  --tile-bg: #f7f2ff;
  --tile-border: #e4d6ff;
  --tile-text: #6f42c1;
  --tile-icon-bg: #eee4ff;
}

.text-purple { color: #6f42c1; }
</style>
